import { CommonModule, DOCUMENT } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, OnInit, OnDestroy, HostListener, ElementRef, NgZone, inject,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { EventService, ReactionType } from '../../shared/services/event.service';
import { CommentService, IComment, IReply } from '../../shared/services/comment.service';
import { UserService } from '../../shared/services/user.service';
// import { ReactionSoundService } from '../../shared/services/reaction-sound.service';
import { DropdownItemComponent } from '../../components/ui/dropdown/dropdown-item/dropdown-item';
import { DropdownComponent } from '../../components/ui/dropdown/dropdown';
import { FriendRequestService, FriendshipStatus } from '../../shared/services/friend-request.service';
import { Modal } from '../../components/ui/modal/modal';
import { MentionInputDirective, MentionUser } from '../../shared/directives/mention-input.directive';
import { SwipeDirective } from '../../shared/directives/swipe.directive';
import { Events } from '../events/events';
import { MentionDropdownComponent } from '../../components/ui/dropdown/mention-dropdown/mention-dropdown.component';
import { MentionHighlightPipe } from '../../shared/pipes/mention-highlight.pipe';
import { RelationshipService } from '../../shared/services/relationship.service';
import { ConfirmationPopup } from '../../components/ui/confirmation-popup/confirmation-popup';
import { AuthStateService } from '../../shared/states/auth.state';
import { FavouriteService } from '../../shared/services/favourites.service';
import { ShareSheetComponent } from '../../components/ui/share-sheet/share-sheet';
import { ShareConfig } from '../../shared/services/share.service';
import { EditEventService, IEditEventRequest, IEditEventUser, IEditEventMediaItem } from '../../shared/services/edit-event.service';
import { CollabrationRequestService } from '../../shared/services/collabration-request.service';
import { FriendRequestLimitPopup } from '../../components/ui/friend-request-limit-popup/friend-request-limit-popup';
import { SeoService } from '../../shared/services/seo.service';
import { AdminPreviewService } from '../../shared/services/admin-preview.service';
import { ReportModal } from '../../components/ui/report-modal/report-modal';
import { BlockUserConfirm } from '../../components/ui/block-user-confirm/block-user-confirm';
import { ReportService } from '../../shared/services/report.service';

export const REACTIONS: {
  type: ReactionType;
  emoji: string;
  label: string;
  color: string;
  bg: string;
}[] = [
    { type: 'LIKE', emoji: '👍', label: 'Like', color: '#2078f4', bg: '#e7f3ff' },
    { type: 'HEART', emoji: '❤️', label: 'Love', color: '#f33e58', bg: '#ffe7eb' },
    { type: 'HUG', emoji: '🤗', label: 'Hug', color: '#f7b125', bg: '#fff4e0' },
    { type: 'WOW', emoji: '😮', label: 'Wow', color: '#f7b125', bg: '#fff4e0' },
    { type: 'SAD', emoji: '😢', label: 'Sad', color: '#7c93a3', bg: '#eef2f5' },
    { type: 'HAHA', emoji: '😂', label: 'Haha', color: '#f7b125', bg: '#fff4e0' },
  ];

export const QUICK_EMOJIS = ['😀', '❤️', '😄', '😂', '😍', '😘', '😈', '😡', '🔥', '👍', '🥺', '💔', '👏'];

export const EMOJI_CATEGORIES: { label: string; emojis: string[] }[] = [
  {
    label: 'Smileys',
    emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌',
      '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭',
      '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔'],
  },
  {
    label: 'Gestures',
    emojis: ['👍', '👎', '👊', '✊', '🤛', '🤜', '🤞', '✌️', '🤟', '🤘', '👌', '🤌', '🤏', '👈',
      '👉', '👆', '👇', '☝️', '👋', '🤚', '🖐', '✋', '🖖', '💪', '🦾', '🤲', '👐', '🙌', '👏'],
  },
  {
    label: 'Hearts',
    emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓',
      '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '☯️', '🕉', '✡️', '🔯', '🛐', '⛎', '♈'],
  },
  {
    label: 'Activities',
    emojis: ['🎉', '🎊', '🎈', '🎁', '🎂', '🍰', '🥂', '🍾', '🎆', '🎇', '✨', '🌟', '⭐', '🌠',
      '🎯', '🎮', '🕹', '🎲', '🎪', '🎭', '🎨', '🖼', '🎬', '🎤', '🎧', '🎵', '🎶', '🎸'],
  },
];

interface ReactionCounts {
  LIKE: number; HEART: number; HUG: number;
  WOW: number; SAD: number; HAHA: number;
}

interface EventDetailCollaborator {
  _id: string;
  displayName: string;
  userName: string;
  profileImage?: { urlSmall?: string };
  friendship: { status: string; requestId?: string | null };
  isFollowed: boolean;
  isMe: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────
@Component({
  selector: 'app-event-details',
  imports: [
    CommonModule,
    ShareSheetComponent,
    FormsModule,
    DropdownComponent,
    DropdownItemComponent,
    Modal,
    MentionInputDirective,       // ← new
    MentionDropdownComponent,    // ← new
    MentionHighlightPipe,
    RouterModule,
    ConfirmationPopup,
    FriendRequestLimitPopup,
    SwipeDirective,
    Events,
    ReportModal,
    BlockUserConfirm
  ],
  templateUrl: './event-details.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventDetails implements OnInit, OnDestroy {

  activeTab: 'details' | 'comments' = 'details';
  eventDetails: any[] | null = null;
  isLoading = true;
  hasError = false;
  readonly REACTIONS = REACTIONS;
  readonly QUICK_EMOJIS = QUICK_EMOJIS;
  readonly EMOJI_CATEGORIES = EMOJI_CATEGORIES;

  /** Raw recommendation items, passed straight to <app-events> to render. */
  recommendedRawItems: any[] = [];
  showCollabModal = false;
  selectedEvent: any | null = null;
  showTaggedModal = false;

  // ── Comment state ─────────────────────────────────────────────────────────
  comments: IComment[] = [];
  commentsTotal = 0;
  commentsPage = 1;
  commentsTotalPages = 1;
  commentsLoading = false;
  commentsLoaded = false;

  commentInput = '';
  replyInputs: Record<string, string> = {};
  replyingTo: string | null = null;

  editingCommentId: string | null = null;
  editInput = '';

  submitting = false;
  openDropdownId: string | null = null;
  togglingComments = false;

  // ── Emoji & GIF ───────────────────────────────────────────────────────────
  showEmojiPicker = false;
  activeEmojiTab = 0;
  activeEmojiContext: string | null = null;
  showGifPicker = false;
  gifSearchQuery = '';
  gifResults: { id: string; url: string; preview: string }[] = [];
  gifLoading = false;
  selectedGifUrl: string | null = null;
  replyGifUrls: Record<string, string> = {};

  // ── @mention state ────────────────────────────────────────────────────────
  // 'main' = main comment input, '<commentId>' = that reply input, null = closed
  activeMentionContext: string | null = null;
  mentionUsers: MentionUser[] = [];
  showMentionDropdown = false;

  // Reference to the main contenteditable div — used to clear it after submit
  @ViewChild('mainDir') mainDirRef?: MentionInputDirective;
  // Reference to the currently open reply contenteditable (only one at a time)
  @ViewChild('replyDir') replyDirRef?: MentionInputDirective;

  private mention$ = new Subject<string>();
  private mentionSub = this.mention$
    .pipe(debounceTime(200), distinctUntilChanged())
    .subscribe(q => this.fetchMentionUsers(q));

  private destroy$ = new Subject<void>();

  private eventId: string | null = null;
  private GIPHY_KEY = 'mpVQUxeAA36qhojmJ6nnsrC6lekAAFfR';

  private friendRequestService = inject(FriendRequestService);
  private relationshipService = inject(RelationshipService);
  private seoService = inject(SeoService);
  private adminPreviewService = inject(AdminPreviewService);

  /** Opened from the admin panel via a preview link (not by the author) —
   *  the removed-by-admin banner then uses admin wording. */
  get isAdminPreview(): boolean {
    return !!this.adminPreviewService.findGrant('EVENT', this.eventId);
  }

  private authState = inject(AuthStateService);
  private favouriteService = inject(FavouriteService);
  private editEventService = inject(EditEventService);
  private document = inject(DOCUMENT);

  isFavourite = false;
  private openEditOnLoad = false;
  private openCommentsOnLoad = false;

  // ── Share sheet ──────────────────────────────────────────────────────────
  shareConfig: ShareConfig | null = null;
  shareCount = 0;

  // ── Friend request cooldown popup state ────────────────────────────────────
  showFriendLimitPopup = false;
  friendLimitHours: number | null = null;

  // ── Login prompt state ─────────────────────────────────────────────────────
  showLoginPrompt = false;
  loginPromptUserName = '';
  loginPromptAction = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventService: EventService,
    private commentService: CommentService,
    private userService: UserService,       // ← new
    private cdr: ChangeDetectorRef,
    // private soundService: ReactionSoundService,
    private elRef: ElementRef,
    private ngZone: NgZone,
    private sanitizer: DomSanitizer,
  ) { }

  /** Renders the WYSIWYG event description with its formatting (bold, italic,
   *  lists, links, etc.) intact — Angular's default [innerHTML] sanitizer
   *  strips markup it can't verify as safe, so we explicitly trust content
   *  that already passed through the backend's sanitize-html allowlist. */
  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html ?? '');
  }

  /** Clicking a #hashtag chip takes the user to the "All" page, filtered to
   *  that exact hashtag across posts, blogs and events. */
  goToHashtag(tag: string, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/all'], { queryParams: { hashtag: tag } });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (!id) return;

      // Angular's server-side `document` has a defaultView but no real
      // scrollTo implementation, so the optional-chaining alone isn't
      // enough — a truthy defaultView with a missing method still throws.
      if (typeof this.document.defaultView?.scrollTo === 'function') {
        this.document.defaultView.scrollTo({ top: 0, behavior: 'smooth' });
      }

      this.eventId = id;
      this.eventDetails = null;
      this.isLoading = true;
      this.hasError = false;
      this.recommendedRawItems = [];

      this.comments = [];
      this.commentsTotal = 0;
      this.commentsPage = 1;
      this.commentsTotalPages = 1;
      this.commentsLoading = false;
      this.commentsLoaded = false;
      this.commentInput = '';
      this.replyInputs = {};
      this.replyingTo = null;
      this.selectedGifUrl = null;
      this.replyGifUrls = {};
      this.showGifPicker = false;
      this.showEmojiPicker = false;
      this.activeTab = 'details';

      this.openEditOnLoad = this.route.snapshot.queryParamMap.get('openEdit') === '1';
      this.openCommentsOnLoad = this.route.snapshot.queryParamMap.get('openComments') === '1';
      this.closeMentionDropdown();
      this.cdr.markForCheck();

      this.getEvent(id);
      this.loadRecommendations(id);
    });

    this.eventService.onRefreshEvents$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.eventId) this.getEvent(this.eventId);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── @mention methods ──────────────────────────────────────────────────────

  /** Called by (mentionSearch) output on any input */
  onMentionSearch(query: string, context: string): void {
    this.activeMentionContext = context;
    this.mention$.next(query);
  }

  /** Called by (mentionDropdown) output — opens or closes per context */
  onMentionDropdownChange(open: boolean, context: string): void {
    if (open) {
      this.activeMentionContext = context;
      this.showMentionDropdown = true;
      if (this.mentionUsers.length === 0) this.fetchMentionUsers('');
    } else if (this.activeMentionContext === context) {
      this.closeMentionDropdown();
    }
    this.cdr.markForCheck();
  }

  private fetchMentionUsers(q: string): void {
    this.userService.searchUsersForMention(q).subscribe({
      next: (res: any) => {
        this.mentionUsers = res?.data ?? [];
        this.showMentionDropdown = this.mentionUsers.length > 0;
        this.cdr.markForCheck();
      },
      error: () => { this.cdr.markForCheck(); },
    });
  }

  /** Called by (selected) output on app-mention-dropdown */
  onMentionPicked(user: MentionUser, context: string, directive: MentionInputDirective): void {
    if (context === 'main') {
      this.commentInput = directive.insertMention(user, this.commentInput);
    } else {
      this.replyInputs[context] = directive.insertMention(user, this.replyInputs[context] ?? '');
    }
    this.closeMentionDropdown();
    this.cdr.markForCheck();
  }

  private closeMentionDropdown(): void {
    this.showMentionDropdown = false;
    this.activeMentionContext = null;
    this.mentionUsers = [];
  }

  // ── Everything below is IDENTICAL to your existing code ──────────────────

  goToAllEvents(): void {
    this.router.navigate(['/events']);
  }

  goToCategory(category: string): void {
    if (!category) return;
    this.router.navigate(['/events'], { queryParams: { categories: category } });
  }

  private loadRecommendations(eventId: string): void {
    this.eventService.getRecommendedEvents(eventId).subscribe({
      next: (res: any) => {
        // Handed to <app-events> raw — it runs its own mapEvents() over the same
        // API shape, so the recommended cards are built by exactly the code that
        // builds the listing cards.
        this.recommendedRawItems = res?.data ?? [];
        this.cdr.markForCheck();
      },
      error: () => { this.cdr.markForCheck(); },
    });
  }

  toggleComments(): void {
    if (!this.eventDetails?.[0] || this.togglingComments) return;
    const ev = this.eventDetails[0];
    const eventId = ev.contentId ?? ev.id;
    this.togglingComments = true;
    this.eventService.toggleComments(eventId).subscribe({
      next: (res: any) => {
        ev.commentsDisabled = res.data?.commentsDisabled ?? !ev.commentsDisabled;
        this.togglingComments = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.togglingComments = false;
        this.cdr.markForCheck();
      }
    });
  }

  friendButtonTitle(status: FriendshipStatus): string {
    const map: Record<FriendshipStatus, string> = {
      NONE: 'Send friend request',
      PENDING_SENT: 'Cancel friend request',
      PENDING_RECEIVED: 'Accept friend request',
      FRIENDS: 'Unfriend',
      BLOCKED: 'Blocked',
    };
    return map[status];
  }

  onFriendButtonClick(card: any, event: MouseEvent): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getEventDisplayName(card), 'add as a friend');
      return;
    }
    event.stopPropagation();
    switch (card.friendshipStatus) {
      case 'NONE': this.sendFriendRequest(card, event); break;
      case 'PENDING_SENT': this.cancelFriendRequest(card, event); break;
      case 'PENDING_RECEIVED': this.acceptFriendRequest(card, event); break;
      case 'FRIENDS': this.promptUnfriend(card, event); break;
      case 'BLOCKED': break;
    }
  }

  showUnfriendConfirm = false;
  private pendingUnfriendAction: (() => void) | null = null;

  private promptUnfriend(card: any, event: MouseEvent): void {
    event.stopPropagation();
    this.pendingUnfriendAction = () => this.unfriend(card, event);
    this.showUnfriendConfirm = true;
  }

  confirmUnfriend(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction?.();
    this.pendingUnfriendAction = null;
  }

  cancelUnfriendConfirm(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction = null;
  }

  /** They've sent you a request — clicking the friend icon accepts it. (It
   *  used to call sendFriendRequest, which bails unless the status is NONE,
   *  so the button did nothing — SAF-839.) */
  private acceptFriendRequest(card: any, event: MouseEvent): void {
    event.stopPropagation();
    if (card.friendshipStatus !== 'PENDING_RECEIVED' || !card.friendshipRequestId) return;
    card.friendshipStatus = 'FRIENDS';
    this.cdr.markForCheck();
    this.friendRequestService.accept(card.friendshipRequestId, card.hostId).subscribe({
      error: () => { card.friendshipStatus = 'PENDING_RECEIVED'; this.cdr.markForCheck(); },
    });
  }

  private sendFriendRequest(card: any, event: MouseEvent): void {
    event.stopPropagation();
    if (card.friendshipStatus !== 'NONE') return;
    card.friendshipStatus = 'PENDING_SENT';
    this.cdr.markForCheck();
    this.friendRequestService.send(card.hostId).subscribe({
      next: (res: any) => { card.friendshipRequestId = res?.data?._id ?? null; this.cdr.markForCheck(); },
      error: (err) => {
        const status = err?.error?.statusCode ?? err?.status;
        if (status !== 409) card.friendshipStatus = 'NONE';
        const cooldownHours = this.friendRequestService.parseCooldownError(err);
        if (cooldownHours !== null) {
          this.friendLimitHours = cooldownHours;
          this.showFriendLimitPopup = true;
        }
        this.cdr.markForCheck();
      },
    });
  }

  private cancelFriendRequest(card: any, event: MouseEvent): void {
    event.stopPropagation();
    if (!card.friendshipRequestId) return;
    const prevStatus = card.friendshipStatus;
    const prevRequestId = card.friendshipRequestId;
    card.friendshipStatus = 'NONE';
    this.cdr.markForCheck();
    this.friendRequestService.cancel(card.friendshipRequestId).subscribe({
      error: () => { card.friendshipStatus = prevStatus; card.friendshipRequestId = prevRequestId; this.cdr.markForCheck(); },
    });
  }

  private unfriend(card: any, event: MouseEvent): void {
    event.stopPropagation();
    const prevStatus = card.friendshipStatus;
    const prevRequestId = card.friendshipRequestId;
    card.friendshipStatus = 'NONE';
    card.friendshipRequestId = null;
    this.cdr.markForCheck();
    this.friendRequestService.unfriend(card.hostId).subscribe({
      error: () => { card.friendshipStatus = prevStatus; card.friendshipRequestId = prevRequestId; this.cdr.markForCheck(); },
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    if (!this.elRef.nativeElement.querySelector('.comment-picker-area')?.contains(target)) {
      this.showEmojiPicker = false;
      this.showGifPicker = false;
      this.closeMentionDropdown();
      this.cdr.markForCheck();
    }
  }

  getEvent(id: string): void {
    this.isLoading = true;
    this.hasError = false;
    this.eventService.getEventDetails(id).subscribe({
      next: (res: any) => {
        this.eventDetails = this.mapEvents([res.data]);
        this.isFavourite = (res.data as any)?.isFavourite ?? false;
        this.isLoading = false;

        const event = this.eventDetails[0];
        if (event) {
          this.seoService.setSocialTags({
            title: event.title || `${event.hostName}'s event`,
            description: event.description || `Event by ${event.hostName} on Safeword`,
            image: event.images?.[0],
            path: `/event/${id}`,
          });
        }

        this.cdr.detectChanges();

        if (this.openEditOnLoad && this.eventDetails?.[0]?.isMyPostedEvent) {
          this.openEditOnLoad = false;
          setTimeout(() => this.editEvent(), 300);
        }

        if (this.openCommentsOnLoad) {
          this.openCommentsOnLoad = false;
          this.switchTab('comments');
        }
      },
      error: (err) => {
        console.error(err);
        this.isLoading = false;
        this.hasError = true;
        this.cdr.markForCheck();
      }
    });
  }

  private mapEvents(apiEvents: any[]): any[] {
    return apiEvents.map((ev: any) => {
      const acceptedCollaborators: EventDetailCollaborator[] = (ev?.info?.collaborations ?? [])
        .filter((collab: any) => collab.status === 'ACCEPTED')
        .map((collab: any) => ({
          _id: collab.user?._id ?? '',
          displayName: collab.user?.displayName ?? '',
          userName: collab.user?.userName ?? '',
          profileImage: collab.user?.profileImage ?? undefined,
          friendship: collab.friendship ?? collab.user?.friendship ?? { status: 'NONE', requestId: null },
          isFollowed: collab.isFollowed ?? collab.user?.isFollowed ?? false,
          isMe: collab.isMe ?? false,
        }));

      const raw = ev?.reactions?.myReaction;
      const rawType: string | null =
        raw && typeof raw === 'object' ? (raw.type ?? null) : (raw ?? null);
      const myReaction: ReactionType | null =
        rawType && REACTIONS.some(r => r.type === rawType)
          ? (rawType as ReactionType)
          : null;

      return {
        id: ev._id,
        contentId: ev._id,
        hostId: ev?.user?._id ?? '',
        hostName: ev?.user?.userName ?? 'Unknown',
        hostAvatar: ev?.user?.profileImage?.urlSmall ?? '',
        timeAgo: this.getTimeAgo(ev?.createdAt),
        isMyPostedEvent: ev?.isMyPostedEvent,
        isRemovedByAdmin: ev?.isRemovedByAdmin ?? false,
        images: ev?.media?.length
          ? ev.media.map((m: any) => m.urlMedium || m.url)
          : [ev?.info?.logo].filter(Boolean),
        activeSlide: 0,
        hashtags: ev?.hashtags ?? [],
        title: ev?.title ?? '',
        description: ev?.description ?? '',
        ...this.formatEventDateLines(ev?.info?.startDate, ev?.info?.endDate),
        location: ev?.info?.location ?? '',
        minPrice: Number(ev?.info?.priceMin || 0),
        maxPrice: Number(ev?.info?.priceMax || 0),
        category: ev?.info?.genreOfEvent ?? '',
        likes: ev?.reactions?.total ?? 0,
        comments: ev?.numberOfComments ?? 0,
        shares: ev?.numberOfShares ?? 0,
        createdAt: ev?.createdAt ?? '',
        myReaction,
        reactionCounts: ev?.reactions?.counts ?? { LIKE: 0, HEART: 0, HUG: 0, WOW: 0, SAD: 0, HAHA: 0 },
        totalReactions: ev?.reactions?.total ?? 0,
        showEmojiPicker: false,
        hoveredReaction: null,
        friendshipStatus: (ev?.friendship?.status ?? 'NONE') as FriendshipStatus,
        friendshipRequestId: ev?.friendship?.requestId ?? null,
        isFollowed: ev.isFollowed ?? false,
        acceptedCollaborators,
        commentsDisabled: ev.info?.commentsDisabled ?? false,
        rawCollaborations: ev?.info?.collaborations ?? [],
        // ── Raw fields preserved for edit modal ──────────────────────────
        info: ev?.info ?? null,
        media: ev?.media ?? [],
        rawMentions: ev?.info?.mentions ?? [],
        linkToBookingSite: ev?.info?.linkToBookingSite ?? null,
        isFavourite: (ev as any)?.isFavourite ?? false
      };
    }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  showCollaboratorsModal(event: any): void {
    this.selectedEvent = event;
    this.showCollabModal = true;
    this.cdr.markForCheck();
  }

  closeCollaboratorsModal(): void {
    this.showCollabModal = false;
    this.selectedEvent = null;
    this.cdr.markForCheck();
  }

  showTaggedPeopleModal(event: any, e?: Event): void {
    e?.stopPropagation();
    this.selectedEvent = event;
    this.showTaggedModal = true;
    this.cdr.markForCheck();
  }

  closeTaggedPeopleModal(): void {
    this.showTaggedModal = false;
    this.selectedEvent = null;
    this.cdr.markForCheck();
  }

  toggleDropdown(commentId: string): void {
    this.openDropdownId = this.openDropdownId === commentId ? null : commentId;
    this.cdr.markForCheck();
  }

  closeDropdown(): void {
    this.openDropdownId = null;
    this.cdr.markForCheck();
  }

  switchTab(tab: 'details' | 'comments'): void {
    this.activeTab = tab;
    if (tab === 'comments' && !this.commentsLoaded && !this.commentsLoading && this.eventId) {
      this.loadComments();
    }
    this.cdr.markForCheck();
  }

  loadComments(page = 1): void {
    if (!this.eventId) return;
    this.commentsLoading = true;
    this.cdr.markForCheck();

    this.commentService.getComments(this.eventId, page, "EVENT").subscribe({
      next: (res: any) => {
        this.ngZone.run(() => {
          if (page === 1) {
            this.comments = res.data.items ?? [];
          } else {
            this.comments = [...this.comments, ...(res.data.items ?? [])];
          }
          this.commentsTotal = res.data.meta.total ?? 0;
          this.commentsPage = res.data.meta.page ?? 1;
          this.commentsTotalPages = res.data.meta.totalPages ?? 1;
          this.commentsLoading = false;
          this.commentsLoaded = true;
          this.cdr.markForCheck();
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.commentsLoading = false;
          this.commentsLoaded = true;
          this.cdr.markForCheck();
        });
      },
      complete: () => {
        if (this.commentsLoading) {
          this.ngZone.run(() => { this.commentsLoading = false; this.cdr.markForCheck(); });
        }
      },
    });
  }

  loadMoreComments(): void {
    if (this.commentsPage < this.commentsTotalPages) {
      this.loadComments(this.commentsPage + 1);
    }
  }

  submitComment(): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getEventDisplayName(this.eventDetails?.[0] ?? {}), 'comment on this event by');
      return;
    }
    const body = this.commentInput.trim();
    if ((!body && !this.selectedGifUrl) || !this.eventId || this.submitting) return;

    const finalBody = this.selectedGifUrl
      ? (body ? `${body}\n${this.selectedGifUrl}` : this.selectedGifUrl)
      : body;

    this.submitting = true;
    this.cdr.markForCheck();

    this.commentService.createComment({
      targetType: 'EVENT',
      targetId: this.eventId,
      body: finalBody,
    }).subscribe({
      next: (res: any) => {
        const newComment: IComment = {
          ...res.data,
          replies: [],
          ableToReply: true,
          ableToModification: true,
        };
        this.comments = [newComment, ...this.comments];
        this.commentsTotal++;
        this.commentInput = '';
        // Clear the contenteditable div
        if (this.mainDirRef) this.mainDirRef.setText('');
        this.selectedGifUrl = null;
        this.showEmojiPicker = false;
        this.showGifPicker = false;
        this.submitting = false;
        this.closeMentionDropdown();
        if (this.eventDetails?.[0]) {
          this.eventDetails[0].comments = (this.eventDetails[0].comments ?? 0) + 1;
        }
        this.cdr.markForCheck();
      },
      error: () => { this.submitting = false; this.cdr.markForCheck(); }
    });
  }

  submitReply(parentId: string): void {
    const body = (this.replyInputs[parentId] ?? '').trim();
    const gifUrl = this.replyGifUrls[parentId] ?? null;
    if ((!body && !gifUrl) || !this.eventId || this.submitting) return;

    const finalBody = gifUrl
      ? (body ? `${body}\n${gifUrl}` : gifUrl)
      : body;

    this.submitting = true;
    this.cdr.markForCheck();

    this.commentService.createComment({
      targetType: 'EVENT',
      targetId: this.eventId,
      parentId,
      body: finalBody,
    }).subscribe({
      next: (res: any) => {
        const newReply: IReply = { ...res.data, ableToModification: true };
        const parent = this.comments.find(c => c._id === parentId);
        if (parent) {
          parent.replies = [newReply, ...(parent.replies ?? [])];
          parent.replyCount = (parent.replyCount ?? 0) + 1;
        }
        if (this.eventDetails?.[0]) {
          this.eventDetails[0].comments = (this.eventDetails[0].comments ?? 0) + 1;
        }
        this.replyInputs[parentId] = '';
        delete this.replyGifUrls[parentId];
        this.replyingTo = null;
        if (this.replyDirRef) this.replyDirRef.setText('');
        this.showGifPicker = false;
        this.submitting = false;
        this.closeMentionDropdown();
        this.cdr.markForCheck();
      },
      error: () => { this.submitting = false; this.cdr.markForCheck(); }
    });
  }

  startEdit(item: IComment | IReply): void {
    this.editingCommentId = item._id;
    this.editInput = item.body;
    this.cdr.markForCheck();
  }

  cancelEdit(): void {
    this.editingCommentId = null;
    this.editInput = '';
    this.cdr.markForCheck();
  }

  saveEdit(item: IComment | IReply): void {
    const body = this.editInput.trim();
    if (!body || this.submitting) return;
    this.submitting = true;
    this.cdr.markForCheck();
    this.commentService.updateComment(item._id, body).subscribe({
      next: (res: any) => {
        item.body = res.data.body;
        item.isEdited = true;
        this.editingCommentId = null;
        this.editInput = '';
        this.submitting = false;
        this.cdr.markForCheck();
      },
      error: () => { this.submitting = false; this.cdr.markForCheck(); }
    });
  }

  deleteComment(commentId: string, parentId?: string): void {
    this.commentService.deleteComment(commentId).subscribe({
      next: (res: any) => {
        if (parentId) {
          const parent = this.comments.find(c => c._id === parentId);
          if (parent) {
            parent.replies = parent.replies.filter(r => r._id !== commentId);
            parent.replyCount = Math.max(0, (parent.replyCount ?? 1) - 1);
          }
        } else {
          this.comments = this.comments.filter(c => c._id !== commentId);
          this.commentsTotal = Math.max(0, this.commentsTotal - 1);
        }
        // The API returns the real count (deleting a comment also removes its
        // replies), so use that rather than guessing locally.
        if (this.eventDetails?.[0]) {
          this.eventDetails[0].comments = res?.data?.numberOfComments
            ?? Math.max(0, (this.eventDetails[0].comments ?? 0) - 1);
        }
        this.cdr.markForCheck();
      }
    });
  }

  // ── Report comment modal ────────────────────────────────────────────────
  isCommentReportModalOpen = false;
  isSubmittingCommentReport = false;
  commentReportError = '';
  commentReportSuccessOpen = false;
  private reportingCommentId: string | null = null;

  canReportComment(comment: { ableToModification: boolean }): boolean {
    return this.isLoggedIn() && !comment.ableToModification;
  }

  reportComment(commentId: string): void {
    this.closeDropdown();
    this.reportingCommentId = commentId;
    this.commentReportError = '';
    this.isCommentReportModalOpen = true;
    this.cdr.markForCheck();
  }

  closeCommentReportModal(): void {
    if (this.isSubmittingCommentReport) return;
    this.isCommentReportModalOpen = false;
    this.commentReportError = '';
    this.cdr.markForCheck();
  }

  submitCommentReport(description: string): void {
    if (!this.reportingCommentId) return;
    this.isSubmittingCommentReport = true;
    this.commentReportError = '';

    this.reportService.submitCommentReport(this.reportingCommentId, description).subscribe({
      next: () => {
        this.isSubmittingCommentReport = false;
        this.isCommentReportModalOpen = false;
        this.commentReportSuccessOpen = true;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isSubmittingCommentReport = false;
        this.commentReportError = err?.error?.message ?? 'Failed to submit report. Please try again.';
        this.cdr.markForCheck();
      },
    });
  }

  closeCommentReportSuccess(): void {
    this.commentReportSuccessOpen = false;
    this.cdr.markForCheck();
  }

  toggleReply(commentId: string): void {
    this.replyingTo = this.replyingTo === commentId ? null : commentId;
    if (this.replyingTo && !this.replyInputs[commentId]) {
      this.replyInputs[commentId] = '';
    }
    this.cdr.markForCheck();
    if (this.replyingTo) {
      setTimeout(() => {
        document.getElementById('reply-input-' + commentId)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }

  appendQuickEmoji(emoji: string): void {
    if (this.editingCommentId !== null) {
      this.editInput += emoji;
    } else if (this.replyingTo) {
      const id = this.replyingTo;
      this.replyInputs[id] = (this.replyInputs[id] ?? '') + emoji;
      if (this.replyDirRef) this.replyDirRef.setText(this.replyInputs[id]);
    } else {
      this.commentInput += emoji;
      // Sync contenteditable div
      if (this.mainDirRef) this.mainDirRef.setText(this.commentInput);
    }
    this.cdr.markForCheck();
  }

  toggleEmojiPicker(e: MouseEvent, context: 'main' | string = 'main'): void {
    e.stopPropagation();
    this.activeEmojiContext = context;
    this.showEmojiPicker = !this.showEmojiPicker;
    this.showGifPicker = false;
    this.cdr.markForCheck();
  }

  pickEmoji(emoji: string): void {
    if (this.editingCommentId !== null) {
      this.editInput += emoji;
    } else if (this.activeEmojiContext && this.activeEmojiContext !== 'main') {
      const id = this.activeEmojiContext;
      this.replyInputs[id] = (this.replyInputs[id] ?? '') + emoji;
      if (this.replyDirRef) this.replyDirRef.setText(this.replyInputs[id]);
    } else {
      this.commentInput += emoji;
      // Sync contenteditable div
      if (this.mainDirRef) this.mainDirRef.setText(this.commentInput);
    }
    this.showEmojiPicker = false;
    this.cdr.markForCheck();
  }

  setEmojiTab(index: number): void {
    this.activeEmojiTab = index;
    this.cdr.markForCheck();
  }

  toggleGifPicker(context: 'main' | string = 'main', e: MouseEvent): void {
    e.stopPropagation();
    this.activeEmojiContext = context;
    this.showGifPicker = !this.showGifPicker;
    this.showEmojiPicker = false;
    if (this.showGifPicker && this.gifResults.length === 0) {
      this.searchGifs('trending');
    }
    this.cdr.markForCheck();
  }

  searchGifs(query?: string): void {
    const q = query ?? this.gifSearchQuery.trim();
    if (!q) return;
    this.gifLoading = true;
    this.cdr.markForCheck();

    const url = q === 'trending'
      ? `https://api.giphy.com/v1/gifs/trending?api_key=${this.GIPHY_KEY}&limit=20&rating=g`
      : `https://api.giphy.com/v1/gifs/search?api_key=${this.GIPHY_KEY}&q=${encodeURIComponent(q)}&limit=20&rating=g`;

    fetch(url)
      .then(r => r.json())
      .then((data: any) => {
        this.gifResults = (data.data ?? []).map((g: any) => ({
          id: g.id,
          url: g.images.original.url,
          preview: g.images.fixed_height_small.url,
        }));
        this.gifLoading = false;
        this.cdr.markForCheck();
      })
      .catch(() => { this.gifLoading = false; this.cdr.markForCheck(); });
  }

  selectGif(gif: { url: string; preview: string }): void {
    if (this.activeEmojiContext && this.activeEmojiContext !== 'main') {
      this.replyGifUrls[this.activeEmojiContext] = gif.url;
    } else {
      this.selectedGifUrl = gif.url;
    }
    this.showGifPicker = false;
    this.cdr.markForCheck();
  }

  removeGif(context: 'main' | string = 'main'): void {
    if (context !== 'main') {
      delete this.replyGifUrls[context];
    } else {
      this.selectedGifUrl = null;
    }
    this.cdr.markForCheck();
  }

  onGifSearchKey(e: KeyboardEvent): void {
    if (e.key === 'Enter') this.searchGifs();
  }

  prevSlide(event: any, e: MouseEvent): void {
    e.stopPropagation();
    event.activeSlide = event.activeSlide > 0 ? event.activeSlide - 1 : event.images.length - 1;
    this.cdr.markForCheck();
  }

  nextSlide(event: any, e: MouseEvent): void {
    e.stopPropagation();
    event.activeSlide = event.activeSlide < event.images.length - 1 ? event.activeSlide + 1 : 0;
    this.cdr.markForCheck();
  }

  /** Touch swipe on the hero carousel. `dir` is +1 for next, -1 for previous. */
  onHeroSwipe(dir: 1 | -1): void {
    const hero = this.eventDetails?.[0];
    const total = hero?.images?.length ?? 0;
    if (total <= 1) return;
    hero.activeSlide = (hero.activeSlide + dir + total) % total;
    this.cdr.markForCheck();
  }

  onReactionClick(card: any): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getEventDisplayName(card), 'react to this event by');
      return;
    }
    card.showEmojiPicker = !card.showEmojiPicker;
    // if (card.showEmojiPicker) this.soundService.playPickerOpen();
    this.cdr.markForCheck();
  }

  onEmojiPick(card: any, type: ReactionType, $event: MouseEvent): void {
    $event.stopPropagation();
    card.showEmojiPicker = false;
    card.hoveredReaction = null;
    // this.soundService.playReactionSelect(type);
    this.submitReaction(card, type);
  }

  openBooking(link: string | null): void {
    console.log(link);
    if (link) { window.open(link, '_blank', 'noopener,noreferrer'); }
  }

  closeEmojiPicker(card: any): void {
    card.showEmojiPicker = false;
    card.hoveredReaction = null;
    this.cdr.markForCheck();
  }

  private submitReaction(card: any, type: ReactionType): void {
    const previous = card.myReaction;
    const previousTopTypes = [...(card.topTypes ?? [])];
    const previousRecentUserName = card.recentUserName;

    if (previous === type) {
      card.reactionCounts[type] = Math.max(0, card.reactionCounts[type] - 1);
      card.totalReactions = Math.max(0, card.totalReactions - 1);
      card.myReaction = null;
    } else {
      if (previous) {
        card.reactionCounts[previous] = Math.max(0, card.reactionCounts[previous] - 1);
        card.totalReactions = Math.max(0, card.totalReactions - 1);
      }
      card.reactionCounts[type]++;
      card.totalReactions++;
      card.myReaction = type;
    }
    card.likes = card.totalReactions;

    // Update topTypes and recentUserName immediately
    card.topTypes = this.computeTopTypes(card.reactionCounts);
    const currentUserName = this.authState.getAuthSnapshot()?.user?.userName ?? null;
    if (card.myReaction) {
      card.recentUserName = currentUserName;
    } else if (card.totalReactions === 0) {
      card.recentUserName = null;
    }

    this.cdr.markForCheck();
    this.eventService.reactToEvent(card.id, type).subscribe({
      error: () => {
        card.myReaction = previous;
        card.reactionCounts = { ...card.reactionCounts };
        card.totalReactions = Object.values(card.reactionCounts).reduce((a: any, b: any) => a + b, 0);
        card.likes = card.totalReactions;
        card.topTypes = previousTopTypes;
        card.recentUserName = previousRecentUserName;
        this.cdr.markForCheck();
      }
    });
  }

  private computeTopTypes(counts: ReactionCounts): ReactionType[] {
    return (Object.entries(counts) as [ReactionType, number][])
      .filter(([, count]) => count > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([type]) => type);
  }

  getReactionConfig(type: ReactionType | null): typeof REACTIONS[0] | null {
    if (!type) return null;
    return REACTIONS.find(r => r.type === type) ?? null;
  }

  isGifBody(body: string): boolean {
    return /^https?:\/\/.*\.(gif|giphy\.com)/.test(body.trim());
  }

  getTextPart(body: string): string {
    return body.split('\n').filter(l => !this.isGifBody(l)).join('\n').trim();
  }

  getGifPart(body: string): string | null {
    return body.split('\n').find(l => this.isGifBody(l)) ?? null;
  }

  /** Single source of truth for the ui-avatars fallback used across this page. */
  avatarFallback(name?: string | null): string {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFD4E0&color=D31148`;
  }

  getUserAvatar(item: IComment | IReply): string {
    return item.user?.profileImage?.urlSmall
      ?? this.avatarFallback(item.user?.displayName);
  }

  getTimeAgo(dateString: string): string {
    const now = new Date().getTime();
    const past = new Date(dateString).getTime();
    const diff = Math.floor((now - past) / 1000);
    if (diff < 60) return 'just now';
    const m = Math.floor(diff / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    const w = Math.floor(d / 7);
    if (d < 30) return `${w} week${w > 1 ? 's' : ''} ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo}mo ago`;
    const y = Math.floor(d / 365);
    return `${y}y ago`;
  }

  private formatEventTime(date: Date): string {
    const hours24 = date.getHours();
    const ampm = hours24 >= 12 ? 'pm' : 'am';
    const hours = hours24 % 12 || 12;
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}${ampm}`;
  }

  private ordinalDay(day: number): string {
    if (day >= 11 && day <= 13) return `${day}th`;
    switch (day % 10) {
      case 1: return `${day}st`;
      case 2: return `${day}nd`;
      case 3: return `${day}rd`;
      default: return `${day}th`;
    }
  }

  private formatEventFullDate(date: Date): string {
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' });
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    return `${weekday} ${this.ordinalDay(date.getDate())} ${month} ${date.getFullYear()}`;
  }

  private isSameCalendarDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  /** Builds the event hero's date display: "Start: … / End: …" on two lines when
   *  the event spans more than one calendar day, or "<start> - <end> <date>" on
   *  one line when it starts and finishes the same day. */
  private formatEventDateLines(startStr?: string, endStr?: string): { dateLine1: string; dateLine2: string | null } {
    if (!startStr) return { dateLine1: '', dateLine2: null };
    const start = new Date(startStr);
    if (!endStr) {
      return { dateLine1: `${this.formatEventTime(start)} ${this.formatEventFullDate(start)}`, dateLine2: null };
    }
    const end = new Date(endStr);
    if (this.isSameCalendarDay(start, end)) {
      return {
        dateLine1: `${this.formatEventTime(start)} - ${this.formatEventTime(end)} ${this.formatEventFullDate(start)}`,
        dateLine2: null,
      };
    }
    return {
      dateLine1: `Start: ${this.formatEventTime(start)} ${this.formatEventFullDate(start)}`,
      dateLine2: `End: ${this.formatEventTime(end)} ${this.formatEventFullDate(end)}`,
    };
  }

  isReactionOpen = false;
  reactionData: any = null;
  reactionsLoading = false;
  activeReactionFilter: ReactionType | null = null;
  private activeReactionEventId: string | null = null;

  openReactionModal(card: any): void {
    this.isReactionOpen = true;
    this.activeReactionFilter = null;
    this.activeReactionEventId = card.id;
    this.loadReactions(card.id);
  }

  closeReactionModal(): void {
    this.isReactionOpen = false;
    this.reactionData = null;
    this.activeReactionEventId = null;
  }

  setReactionFilter(type: ReactionType | null): void {
    this.activeReactionFilter = type;
    if (this.activeReactionEventId) {
      this.loadReactions(this.activeReactionEventId, type ?? undefined);
    }
  }

  private loadReactions(eventId: string, type?: ReactionType): void {
    this.reactionsLoading = true;
    this.cdr.detectChanges();
    this.eventService.getReactions(eventId, type).subscribe({
      next: (res: any) => {
        this.reactionData = res?.data;
        this.reactionsLoading = false;
        this.cdr.detectChanges();
      },
      error: () => { this.reactionsLoading = false; this.cdr.detectChanges(); }
    });
  }

  onModalFollowClick(item: any, event: MouseEvent): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(item.user?.userName ?? item.user?.userName, 'follow');
      return;
    }
    event.stopPropagation();
    if (item.isFollowed) {
      item.isFollowed = false;
      this.cdr.markForCheck();
      this.relationshipService.unfollow(item.user?._id).subscribe({
        error: () => { item.isFollowed = true; this.cdr.markForCheck(); }
      });
    } else {
      item.isFollowed = true;
      this.cdr.markForCheck();
      this.relationshipService.follow(item.user?._id).subscribe({
        error: () => { item.isFollowed = false; this.cdr.markForCheck(); }
      });
    }
  }

  // ── Modal: Friend request ─────────────────────────────────────────────────────
  onModalFriendClick(item: any, event: MouseEvent): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(item.user?.userName ?? item.user?.userName, 'add as a friend');
      return;
    }
    event.stopPropagation();
    const status = item.friendship.status ?? 'NONE';

    if (status === 'NONE') {
      item.friendship.status = 'PENDING_SENT';
      this.cdr.markForCheck();
      this.friendRequestService.send(item.user?._id).subscribe({
        next: (res: any) => { item.friendship.requestId = res?.data?._id ?? null; this.cdr.markForCheck(); },
        error: (err: any) => {
          const code = err?.error?.statusCode ?? err?.status;
          if (code !== 409) item.friendship.status = 'NONE';
          const cooldownHours = this.friendRequestService.parseCooldownError(err);
          if (cooldownHours !== null) {
            this.friendLimitHours = cooldownHours;
            this.showFriendLimitPopup = true;
          }
          this.cdr.markForCheck();
        }
      });
    } else if (status === 'PENDING_SENT') {
      const prev = item.friendship.status;
      const prevId = item.friendship.requestId;
      item.friendship.status = 'NONE';
      this.cdr.markForCheck();
      this.friendRequestService.cancel(item.friendship.requestId).subscribe({
        error: () => { item.friendship.status = prev; item.friendship.requestId = prevId; this.cdr.markForCheck(); }
      });
    } else if (status === 'FRIENDS') {
      this.pendingUnfriendAction = () => {
        const prev = item.friendship.status;
        item.friendship.status = 'NONE';
        this.cdr.markForCheck();
        this.friendRequestService.unfriend(item.user?._id).subscribe({
          error: () => { item.friendship.status = prev; this.cdr.markForCheck(); }
        });
      };
      this.showUnfriendConfirm = true;
    }
  }

  modalFriendIcon(status: string): string {
    // Returns which SVG to show: 'add' | 'pending' | 'friends'
    if (status === 'PENDING_SENT') return 'pending';
    if (status === 'FRIENDS') return 'friends';
    return 'add';
  }

  onEventFollowClick(event?: MouseEvent): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getEventDisplayName(this.eventDetails?.[0]), 'follow');
      return;
    }

    event?.stopPropagation();
    if (!this.eventDetails?.[0]) return;
    const ev = this.eventDetails[0];

    if (ev.isFollowed) {
      ev.isFollowed = false;
      this.cdr.markForCheck();
      this.relationshipService.unfollow(ev.hostId).subscribe({
        error: () => { ev.isFollowed = true; this.cdr.markForCheck(); }
      });
    } else {
      ev.isFollowed = true;
      this.cdr.markForCheck();
      this.relationshipService.follow(ev.hostId).subscribe({
        error: () => { ev.isFollowed = false; this.cdr.markForCheck(); }
      });
    }
  }

  onCollabFriendClick(collab: EventDetailCollaborator, event: MouseEvent): void {
    event.stopPropagation();
    const status = collab.friendship.status ?? 'NONE';
    if (status === 'NONE') {
      collab.friendship.status = 'PENDING_SENT';
      this.cdr.markForCheck();
      this.friendRequestService.send(collab._id).subscribe({
        next: (res: any) => { collab.friendship.requestId = res?.data?._id ?? null; this.cdr.markForCheck(); },
        error: (err: any) => {
          const code = err?.error?.statusCode ?? err?.status;
          if (code !== 409) collab.friendship.status = 'NONE';
          const cooldownHours = this.friendRequestService.parseCooldownError(err);
          if (cooldownHours !== null) {
            this.friendLimitHours = cooldownHours;
            this.showFriendLimitPopup = true;
          }
          this.cdr.markForCheck();
        },
      });
    } else if (status === 'PENDING_SENT') {
      const prev = collab.friendship.status;
      const prevId = collab.friendship.requestId;
      collab.friendship.status = 'NONE';
      this.cdr.markForCheck();
      this.friendRequestService.cancel(collab.friendship.requestId!).subscribe({
        error: () => { collab.friendship.status = prev; collab.friendship.requestId = prevId; this.cdr.markForCheck(); },
      });
    } else if (status === 'FRIENDS') {
      this.pendingUnfriendAction = () => {
        const prev = collab.friendship.status;
        collab.friendship.status = 'NONE';
        collab.friendship.requestId = null;
        this.cdr.markForCheck();
        this.friendRequestService.unfriend(collab._id).subscribe({
          error: () => { collab.friendship.status = prev; this.cdr.markForCheck(); },
        });
      };
      this.showUnfriendConfirm = true;
    }
  }

  onCollabFollowClick(collab: EventDetailCollaborator, event: MouseEvent): void {
    event.stopPropagation();
    if (collab.isFollowed) {
      collab.isFollowed = false;
      this.cdr.markForCheck();
      this.relationshipService.unfollow(collab._id).subscribe({
        error: () => { collab.isFollowed = true; this.cdr.markForCheck(); },
      });
    } else {
      collab.isFollowed = true;
      this.cdr.markForCheck();
      this.relationshipService.follow(collab._id).subscribe({
        error: () => { collab.isFollowed = false; this.cdr.markForCheck(); },
      });
    }
  }

  get filteredReactions(): any[] { return this.reactionData?.all ?? []; }

  private isLoggedIn(): boolean {
    return this.authState.getAuthSnapshot()?.isLoggedIn === true;
  }

  private openLoginPrompt(userName: string, action: string): void {
    this.loginPromptUserName = userName;
    this.loginPromptAction = action;
    this.showLoginPrompt = true;
    this.cdr.markForCheck();
  }

  closeLoginPrompt(): void {
    this.showLoginPrompt = false;
    this.cdr.markForCheck();
  }

  goToLogin(): void {
    this.closeLoginPrompt();
    this.router.navigate(['/login']);
  }

  // ── Share ────────────────────────────────────────────────────────────────────

  openShareSheet(): void {
    this.shareCount = this.eventDetails?.[0]?.shares ?? 0;
    this.shareConfig = {
      contentId: this.eventId ?? '',
      contentType: 'event',
      title: this.eventDetails?.[0]?.title ?? 'Check out this event',
      description: this.eventDetails?.[0]?.title,
      imageUrl: this.eventDetails?.[0]?.images?.[0],
    };
    this.cdr.markForCheck();
  }

  closeShareSheet(): void {
    this.shareConfig = null;
    this.cdr.markForCheck();
  }

  onShareCountUpdated(count: number): void {
    this.shareCount = count; // this.eventDetails?.[0]?.shares ?? 0;
    this.shareCount = count;
    this.cdr.markForCheck();
  }

  // ── Favourites ─────────────────────────────────────────────────────────────

  toggleFavourite(): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt('this event', 'save to favourites');
      return;
    }
    const eventId = this.eventId;
    if (!eventId) return;
    const prev = this.isFavourite;
    this.isFavourite = !prev;
    this.cdr.detectChanges();
    this.favouriteService.toggle(eventId).subscribe({
      next: (res: any) => {
        this.isFavourite = res?.data?.isFavourite ?? !prev;
        this.favouriteService.broadcastToggle(eventId, this.isFavourite);
        this.cdr.detectChanges();
      },
      error: () => { this.isFavourite = prev; this.cdr.detectChanges(); },
    });
  }

  getCurrentUserAvatar(): string {
    const user = this.authState.getAuthSnapshot()?.user;

    return user?.profileImage?.urlSmall
      ?? user?.profileImage?.urlMedium
      ?? this.avatarFallback(user?.displayName || user?.userName);
  }

  // builds "hostName & collab1, collab2" from any card
  private getEventDisplayName(card: any): string {
    const collabNames = (card.acceptedCollaborators ?? [])
      .map((c: any) => c.userName)
      .join(', ');
    const name = card.hostName ?? card.user?.name ?? '';
    return collabNames ? `${name} & ${collabNames}` : name;
  }

  // ── Edit / Delete / Leave Collaboration ──────────────────────────────────

  showActionMenu = false;
  showDeleteConfirm = false;
  showLeaveConfirm = false;
  isDeleting = false;
  isLeaving = false;

  // ── Report event modal ───────────────────────────────────────────────────
  isReportModalOpen = false;
  isSubmittingReport = false;
  reportError = '';
  reportSuccessOpen = false;
  private reportService = inject(ReportService);

  // ── Block user (safeword-confirm) modal ─────────────────────────────────
  showBlockUserConfirm = false;

  /** Bound to <app-block-user-confirm>'s [targetName] — "You're about to
   *  block {name}". */
  get eventToBlockDisplayName(): string {
    return this.eventDetails?.[0]?.hostName ?? '';
  }

  get isMyEvent(): boolean {
    return this.eventDetails?.[0]?.isMyPostedEvent ?? false;
  }

  get isCollaborator(): boolean {
    const authUser = this.authState.getAuthSnapshot()?.user;
    const userId = authUser?._id ?? authUser?.id;
    if (!userId || !this.eventDetails?.[0]) return false;
    return (this.eventDetails[0].rawCollaborations ?? []).some(
      (c: any) => {
        const collabUserId = c.user?._id ?? c.user?.id ?? c.user;
        const isMatch = collabUserId === userId || collabUserId?.toString() === userId?.toString();
        // Only show "Leave" for ACCEPTED collaborators — not pending/declined invites
        return isMatch && c.status === 'ACCEPTED';
      },
    );
  }

  get myCollabStatus(): 'PENDING' | 'ACCEPTED' | 'DECLINED' | null {
    const authUser = this.authState.getAuthSnapshot()?.user;
    const userId = authUser?._id ?? authUser?.id;
    if (!userId || !this.eventDetails?.[0]) return null;
    const collab = (this.eventDetails[0].rawCollaborations ?? []).find((c: any) => {
      const id = c.user?._id ?? c.user?.id ?? c.user;
      return id === userId || id?.toString() === userId?.toString();
    });
    return collab?.status ?? null;
  }

  // ── Collaboration accept / decline ────────────────────────────────────────
  private collabService = inject(CollabrationRequestService);
  isCollabActioning = false;

  acceptCollaboration(): void {
    if (this.isCollabActioning || !this.eventDetails?.[0]) return;
    this.isCollabActioning = true;
    this.cdr.markForCheck();
    console.log('Accepting collaboration for event:', this.eventDetails[0]);
    const eventId = this.eventDetails[0].info._id ?? this.eventDetails[0].id;
    this.collabService.eventCollabrationAccept(eventId).subscribe({
      next: () => {
        this.updateMyCollabStatus('ACCEPTED');
        this.isCollabActioning = false;
        this.eventService.triggerRefresh();
        this.cdr.markForCheck();
      },
      error: () => { this.isCollabActioning = false; this.cdr.markForCheck(); },
    });
  }

  declineCollaboration(): void {
    if (this.isCollabActioning || !this.eventDetails?.[0]) return;
    this.isCollabActioning = true;
    this.cdr.markForCheck();
    const eventId = this.eventDetails[0].contentId ?? this.eventDetails[0].id;
    this.collabService.eventCollabrationDecline(eventId).subscribe({
      next: () => {
        this.updateMyCollabStatus('DECLINED');
        this.isCollabActioning = false;
        this.eventService.triggerRefresh();
        this.cdr.markForCheck();
      },
      error: () => { this.isCollabActioning = false; this.cdr.markForCheck(); },
    });
  }

  private updateMyCollabStatus(status: 'ACCEPTED' | 'DECLINED'): void {
    const authUser = this.authState.getAuthSnapshot()?.user;
    const userId = authUser?._id ?? authUser?.id;
    const collab = (this.eventDetails?.[0]?.rawCollaborations ?? []).find((c: any) => {
      const id = c.user?._id ?? c.user?.id ?? c.user;
      return id === userId || id?.toString() === userId?.toString();
    });
    if (collab) collab.status = status;
  }

  toggleActionMenu(e: MouseEvent): void {
    e.stopPropagation();
    this.showActionMenu = !this.showActionMenu;
    this.cdr.markForCheck();
  }

  closeActionMenu(): void {
    this.showActionMenu = false;
    this.cdr.markForCheck();
  }

  editEvent(): void {
    this.closeActionMenu();
    const ev = this.eventDetails?.[0];
    if (!ev) return;

    // Raw info object preserved in mapEvents
    const info = ev.info;

    // ── Tagged people (mentions) ─────────────────────────────────────────────
    const rawMentions: any[] = ev.rawMentions ?? [];
    const taggedPeople: IEditEventUser[] = rawMentions
      .filter((u: any) => u && typeof u === 'object' && u._id)
      .map((u: any) => ({
        _id: u._id ?? '',
        displayName: u.displayName ?? '',
        userName: u.userName ?? '',
        profileImage: u.profileImage ?? null,
      }));

    // ── Collaborators (all statuses so user can see who's pending too) ───────
    const rawCollabs: any[] = ev.rawCollaborations ?? [];
    const collaborators: IEditEventUser[] = rawCollabs
      .filter((c: any) => c.user && typeof c.user === 'object')
      .map((c: any) => ({
        _id: c.user._id ?? '',
        displayName: c.user.displayName ?? '',
        userName: c.user.userName ?? '',
        profileImage: c.user.profileImage ?? null,
        collabStatus: (c.status ?? 'PENDING') as 'ACCEPTED' | 'PENDING' | 'DECLINED',
      }));

    // ── Existing media (full objects from raw media array) ───────────────────
    const rawMedia: any[] = ev.media ?? [];
    const existingMedia: IEditEventMediaItem[] = rawMedia.map((m: any) => ({
      _id: m._id ?? '',
      url: m.urlLarge || m.urlMedium || m.urlSmall || m.url || '',
      urlLarge: m.urlLarge,
      urlMedium: m.urlMedium,
      type: (m.type ?? 'image') as 'image' | 'video',
    }));

    const req: IEditEventRequest = {
      eventId: ev.contentId ?? ev.id,
      title:            info?.title ?? ev.title ?? '',
      description:      info?.description ?? ev.description ?? '',
      startDate:        info?.startDate,
      endDate:          info?.endDate,
      genreOfEvent:     info?.genreOfEvent ?? ev.category ?? '',
      location:         info?.location ?? ev.location ?? '',
      minPrice:         info?.priceMin != null ? String(info.priceMin) : String(ev.minPrice ?? ''),
      maxPrice:         info?.priceMax != null ? String(info.priceMax) : String(ev.maxPrice ?? ''),
      currency:         info?.currency ?? 'GBP',
      linkToBookingSite: info?.linkToBookingSite ?? '',
      hashtags:         ev.hashtags ?? [],
      isExternal:       info?.type === 'EXTERNAL',
      externalLink:     info?.type === 'EXTERNAL' ? (info as any)?.link ?? '' : '',
      taggedPeople,
      collaborators,
      existingMedia,
    };
    this.editEventService.openEditModal(req);
  }

  confirmDelete(): void {
    this.closeActionMenu();
    this.showDeleteConfirm = true;
    this.cdr.markForCheck();
  }

  cancelDelete(): void {
    this.showDeleteConfirm = false;
    this.cdr.markForCheck();
  }

  navigateBack(): void {
    const state = (window.history.state ?? {}) as { returnUrl?: string };
    if (state?.returnUrl) {
      this.router.navigateByUrl(state.returnUrl);
    } else {
      this.router.navigate(['/events']);
    }
  }

  doDeleteEvent(): void {
    const id = this.eventDetails?.[0]?.id;
    if (this.isDeleting || !id) return;
    this.isDeleting = true;
    this.cdr.markForCheck();

    this.eventService.deleteEvent(id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.showDeleteConfirm = false;
        this.eventService.triggerRefresh();
        this.navigateBack();
      },
      error: (err) => {
        this.isDeleting = false;
        this.showDeleteConfirm = false;
        console.error('Delete event error:', err);
        this.cdr.markForCheck();
      },
    });
  }

  confirmLeave(): void {
    this.closeActionMenu();
    this.showLeaveConfirm = true;
    this.cdr.markForCheck();
  }

  cancelLeave(): void {
    this.showLeaveConfirm = false;
    this.cdr.markForCheck();
  }

  doLeaveCollaboration(): void {
    const id = this.eventDetails?.[0]?.id;
    if (this.isLeaving || !id) return;
    this.isLeaving = true;
    this.cdr.markForCheck();

    this.eventService.leaveCollaboration(id).subscribe({
      next: () => {
        this.isLeaving = false;
        this.showLeaveConfirm = false;
        this.eventService.triggerRefresh();
        this.navigateBack();
      },
      error: (err) => {
        this.isLeaving = false;
        this.showLeaveConfirm = false;
        console.error('Leave event collaboration error:', err);
        this.cdr.markForCheck();
      },
    });
  }

  // ── Block user (from the event's 3-dot menu) ──────────────────────────────

  blockEventUser(): void {
    this.closeActionMenu();
    this.showBlockUserConfirm = true;
    this.cdr.markForCheck();
  }

  cancelBlockEventUser(): void {
    this.showBlockUserConfirm = false;
    this.cdr.markForCheck();
  }

  confirmBlockEventUser(): void {
    this.showBlockUserConfirm = false;
    const hostId = this.eventDetails?.[0]?.hostId;
    this.cdr.markForCheck();
    if (!hostId) return;

    this.friendRequestService.block(hostId).subscribe({
      next: () => {
        this.friendRequestService.triggerRefresh();
        // The author's content is no longer visible to us once blocked —
        // this detail page can no longer be shown, so leave it.
        this.navigateBack();
      },
      error: (err) => {
        console.error('Block user failed:', err);
        this.cdr.markForCheck();
      },
    });
  }

  reportEvent(): void {
    this.closeActionMenu();
    this.reportError = '';
    this.isReportModalOpen = true;
    this.cdr.markForCheck();
  }

  closeReportModal(): void {
    if (this.isSubmittingReport) return;
    this.isReportModalOpen = false;
    this.reportError = '';
    this.cdr.markForCheck();
  }

  submitEventReport(description: string): void {
    const id = this.eventId ?? this.eventDetails?.[0]?.contentId ?? this.eventDetails?.[0]?.id;
    if (!id) return;
    this.isSubmittingReport = true;
    this.reportError = '';

    this.reportService.submitReport(id, description).subscribe({
      next: () => {
        this.isSubmittingReport = false;
        this.isReportModalOpen = false;
        this.reportSuccessOpen = true;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isSubmittingReport = false;
        this.reportError = err?.error?.message ?? 'Failed to submit report. Please try again.';
        this.cdr.markForCheck();
      },
    });
  }

  closeReportSuccess(): void {
    this.reportSuccessOpen = false;
    this.cdr.markForCheck();
  }
}